import { useMemo } from "react";
import { Box, Text, Tooltip, ActionIcon, Badge, Group } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { isEmptyHtml } from "../../../utils/helpers";
import { useIcdCodes } from "../../../hooks/useReferenceData";

interface Props {
  caseData: any;
}

function ClinicalField({
  label,
  html,
  bold,
}: {
  label: string;
  html: string | null | undefined;
  bold?: boolean;
}) {
  const hasContent = html && !isEmptyHtml(html);
  return (
    <Box style={{ flex: 1, minWidth: 280, maxWidth: 520 }}>
      <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={4}>
        {label}
      </Text>
      {hasContent ? (
        <Box
          style={{
            fontSize: 14,
            lineHeight: 1.6,
            fontWeight: bold ? 600 : 400,
            color: bold ? "#0b50da" : undefined,
          }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <Text size="sm" c="dimmed" fs="italic">
          —
        </Text>
      )}
    </Box>
  );
}

export default function ClinicalInfoCardV2({ caseData }: Props) {
  const history = caseData?.clinicalHistoryNotes;
  const indications = caseData?.indications;
  const impressions = caseData?.impressions;
  const consultationReason = caseData?.consultationReason;
  const consultationDiagnosis = caseData?.consultationOriginalDiagnosis;
  const requestingPathologist = caseData?.requestingPathologist;
  const frozen = caseData?.frozenInterpretation;
  const ruleOuts: string[] = caseData?.ruleOuts || [];
  const clinicalHistoryCodeIds = useMemo(
    () => (caseData?.clinicalHistoryCodes || []) as string[],
    [caseData?.clinicalHistoryCodes]
  );

  const { data: icdCodesOptions = [] } = useIcdCodes({
    enabled: clinicalHistoryCodeIds.length > 0,
  });

  const resolvedIcdCodes = useMemo(() => {
    if (clinicalHistoryCodeIds.length === 0 || icdCodesOptions.length === 0)
      return [];
    const lookup = new Map(
      icdCodesOptions.map((o: { value: string; label: string }) => [
        o.value,
        o.label,
      ])
    );
    return clinicalHistoryCodeIds
      .map((id) => lookup.get(id))
      .filter(Boolean) as string[];
  }, [clinicalHistoryCodeIds, icdCodesOptions]);

  const hasRuleOuts = ruleOuts.length > 0;
  const hasIcdCodes = resolvedIcdCodes.length > 0;
  const hasIcdCodeIds = clinicalHistoryCodeIds.length > 0;

  const hasAny =
    (history && !isEmptyHtml(history)) ||
    (indications && !isEmptyHtml(indications)) ||
    (impressions && !isEmptyHtml(impressions)) ||
    consultationReason ||
    consultationDiagnosis ||
    requestingPathologist ||
    frozen ||
    hasRuleOuts ||
    hasIcdCodeIds;

  return (
    <Box
      style={{
        background: "white",
        borderRadius: 12,
        border: "1px solid var(--mantine-color-gray-2)",
        overflow: "hidden",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      }}
    >
      <Box
        px="lg"
        py="sm"
        style={{
          borderBottom: "1px solid var(--mantine-color-gray-1)",
          background: "rgba(248, 249, 250, 0.5)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Text size="sm" fw={700} tt="uppercase" lts="0.05em" c="dimmed">
          Clinical Information
        </Text>
        <Tooltip label="Clinical information from the requisition form">
          <ActionIcon variant="subtle" color="gray" size="sm">
            <IconInfoCircle size={16} />
          </ActionIcon>
        </Tooltip>
      </Box>
      <Box p="lg">
        {!hasAny ? (
          <Text size="sm" c="dimmed" fs="italic">
            No clinical information recorded
          </Text>
        ) : (
          <Box style={{ display: "flex", flexWrap: "wrap", gap: 24 }}>
            <ClinicalField label="Clinical History" html={history} />
            <ClinicalField label="Indications" html={indications} />
            <ClinicalField label="Impressions" html={impressions} bold />
            {hasIcdCodeIds && (
              <Box style={{ flex: 1, minWidth: 280, maxWidth: 520 }}>
                <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={4}>
                  Clinical History Codes (ICD-10)
                </Text>
                {hasIcdCodes ? (
                  <Group gap={6} wrap="wrap">
                    {resolvedIcdCodes.map((label) => (
                      <Badge
                        key={label}
                        size="sm"
                        variant="light"
                        color="blue"
                        radius="sm"
                      >
                        {label}
                      </Badge>
                    ))}
                  </Group>
                ) : (
                  <Text size="sm" c="dimmed" fs="italic">
                    Loading codes…
                  </Text>
                )}
              </Box>
            )}
            {hasRuleOuts && (
              <Box style={{ flex: 1, minWidth: 280, maxWidth: 520 }}>
                <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={4}>
                  Rule Outs
                </Text>
                <Text size="sm" style={{ lineHeight: 1.6 }}>
                  {ruleOuts.join(", ")}
                </Text>
              </Box>
            )}
            {consultationReason && (
              <Box style={{ flex: 1, minWidth: 280, maxWidth: 520 }}>
                <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={4}>
                  Consultation Reason
                </Text>
                <Text size="sm">{consultationReason}</Text>
              </Box>
            )}
            {consultationDiagnosis && !isEmptyHtml(consultationDiagnosis) && (
              <ClinicalField
                label="Original Diagnosis"
                html={consultationDiagnosis}
              />
            )}
            {requestingPathologist && (
              <Box style={{ flex: 1, minWidth: 280, maxWidth: 520 }}>
                <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={4}>
                  Requesting Pathologist
                </Text>
                <Text size="sm">{requestingPathologist}</Text>
              </Box>
            )}
            {frozen && (
              <Box style={{ flex: 1, minWidth: 280, maxWidth: 520 }}>
                <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={4}>
                  Frozen Interpretation
                </Text>
                <Box
                  style={{ fontSize: 14, lineHeight: 1.6 }}
                  dangerouslySetInnerHTML={{ __html: frozen }}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
